import React, { memo, useRef, useEffect, useState, useCallback } from 'react';
import { dates, formats, validators } from 'investira.sdk';
import {
  Stack,
  Card,
  CardHeader,
  CardContent,
  Icon,
  Typography,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useMediaQuery
} from 'investira.react.components';
import { StocksChart } from 'investira.react.charts';
import Controls from '../AtivoCharts/Controls';
import { PessoaImage } from '../../../../components/molecules';
import { ATIVO_DISPLAY_MODE } from '../../../../components/organisms/ativoDisplayMode';
import utils from '../../../../utils';

const CHART_CONFIG = {
  [ATIVO_DISPLAY_MODE.bolsa]: {
    title: 'Cotação',
    valueLabel: 'Cotação'
  },
  [ATIVO_DISPLAY_MODE.fundo_listado]: {
    title: 'Preço Mercado',
    valueLabel: 'Preço Mercado'
  },
  [ATIVO_DISPLAY_MODE.fundo_aberto]: {
    title: 'Valor Cota',
    valueLabel: 'Valor cota'
  },
  [ATIVO_DISPLAY_MODE.fundo_fechado]: {
    title: 'Valor Cota',
    valueLabel: 'Valor cota'
  }
};

const AtivoCotacao = memo(props => {
  const {
    ativo,
    cotacao,
    cotacoes,
    isIndicadorLoading,
    displayMode = ATIVO_DISPLAY_MODE.bolsa
  } = props;
  const [ajustado, setAjustado] = useState(true);
  const [options, setOptions] = useState(null);
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));
  const xAtivoDescricao = utils.helpers.getAtivoDescricao(ativo);
  const xAtivoLabel = isUpSm ? xAtivoDescricao : utils.typo.shortname(xAtivoDescricao, 25);
  const xDecimais = ativo?.decimais ? (ativo.decimais > 6 ? 6 : ativo.decimais) : 6;
  const xCotacao =
    ajustado && !validators.isNull(cotacao?.hoje?.cotacao_ajustada)
      ? cotacao.hoje.cotacao_ajustada
      : !validators.isNull(cotacao?.hoje?.cotacao)
      ? cotacao.hoje.cotacao
      : null;
  const xCotacaoData = cotacao?.hoje?.data ? formats.formatDate(cotacao.hoje.data) : null;
  const chartRef = useRef(null);
  const xChartConfig = CHART_CONFIG[displayMode] || CHART_CONFIG[ATIVO_DISPLAY_MODE.bolsa];
  const xResumoCotacao = [
    ...(!validators.isNull(xCotacao) && !validators.isNull(xCotacaoData)
      ? [
          {
            id: 'atual',
            label: xCotacaoData,
            values: [
              {
                id: 'atual',
                currentValue: formats.formatNumber(xCotacao, xDecimais, true, true)
              }
            ]
          }
        ]
      : []),
    ...(!validators.isNull(cotacao?.cotacao_min_12m)
      ? [
          {
            id: 'minimo-12-meses',
            label: 'Min. 12 meses',
            values: [
              {
                id: 'minimo-12-meses',
                currentValue: formats.formatNumber(cotacao.cotacao_min_12m, xDecimais, true, true)
              }
            ]
          }
        ]
      : []),
    ...(!validators.isNull(cotacao?.cotacao_max_12m)
      ? [
          {
            id: 'maximo-12-meses',
            label: 'Máx. 12 meses',
            values: [
              {
                id: 'maximo-12-meses',
                currentValue: formats.formatNumber(cotacao.cotacao_max_12m, xDecimais, true, true)
              }
            ]
          }
        ]
      : []),
    ...(!validators.isNull(cotacao?.variacoes?.umAno)
      ? [
          {
            id: 'variacao-12-meses',
            label: 'Variação em 12 meses',
            values: [
              {
                id: 'variacao-12-meses',
                currentValue: `${formats.formatNumber(cotacao.variacoes.umAno, 2, true, false)}%`
              }
            ]
          }
        ]
      : []),
    ...(!validators.isNull(cotacao?.variacoes?.inicio)
      ? [
          {
            id: 'variacao-inicio',
            label: 'Variação do início',
            values: [
              {
                id: 'variacao-inicio',
                currentValue: `${formats.formatNumber(cotacao.variacoes.inicio, 2, true, false)}%`
              }
            ]
          }
        ]
      : [])
  ];

  console.log('cotacao', cotacao);

  function formatData(pData = [], pAjustado = true) {
    if (validators.isNull(pData)) {
      return [];
    }

    const xCotacao = pAjustado ? 'cotacao_ajustada' : 'cotacao';

    const xFormattedData = pData.map(xPoint => {
      const xDate = dates.toDate(xPoint.data).getTime();
      return [xDate, xPoint[xCotacao]];
    });

    return xFormattedData;
  }

  const createChartOptions = useCallback(
    (pCotacoes, pAjustado = true) => {
      const to = new Date().getTime();
      const from = new Date(to - 365 * 24 * 3600 * 1000).getTime();

      const xSeries = pCotacoes.map(pCotacao => {
        const xData = pCotacao.data;

        return {
          name: pCotacao.ativo,
          type: 'line',
          data: formatData(xData, pAjustado)
        };
      });

      const xOptions = {
        title: false,
        chart: {
          zooming: {
            type: 'x'
          },
          events: {
            load: function () {
              const chart = this;
              chart.xAxis[0].setExtremes(from, to);
            }
          }
        },
        rangeSelector: {
          enabled: false,
          buttons: [
            { type: 'month', count: 1, text: '1M' },
            { type: 'month', count: 6, text: '6M' },
            { type: 'year', count: 1, text: '1A' },
            { type: 'year', count: 2, text: '2A' },
            { type: 'year', count: 3, text: '3A' },
            { type: 'year', count: 5, text: '5A' },
            { type: 'ytd', text: 'YTD' },
            { type: 'all', text: 'MÁX.' }
          ],
          selected: 2
        },
        xAxis: { type: 'datetime', crosshair: true },
        yAxis: {
          type: 'linear',
          opposite: false,
          title: false,
          labels: {
            formatter: function () {
              return `R$ ${this.value}`;
            }
          }
        },
        series: xSeries,
        tooltip: {
          formatter: function (_tooltip) {
            const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
            const xDecimais = ativo.decimais > 6 ? 6 : ativo.decimais;
            const xCotacao = formats.friendlyNumber(this.point.y, xDecimais, true);

            return `
          <span style="font-size: 1em"><b>${xDate}</b></span><br/>
          <span style="color:${this.color}">\u25CF</span>
          ${this.series.name}: <b>${xCotacao}</b><br/>
          ${xChartConfig.valueLabel}: <b>${xCotacao}</b>
          `;
          }
        }
      };

      return xOptions;
    },
    [xChartConfig.valueLabel]
  );

  const handleRangeSelect = (type, count) => {
    const xChart = chartRef.current;
    const xTo = new Date().getTime();
    let xFrom;

    switch (type) {
      case 'month':
        xFrom = new Date(xTo - count * 30 * 24 * 3600 * 1000).getTime();
        break;
      case 'year':
        xFrom = new Date(xTo - count * 365 * 24 * 3600 * 1000).getTime();
        break;
      case 'ytd':
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        xFrom = startOfYear;
        break;
      case 'all':
        xFrom = null;
        break;
      default:
        return;
    }

    xChart.xAxis[0].setExtremes(xFrom, xTo);
  };

  useEffect(() => {
    const xOptions = createChartOptions(cotacoes, ajustado);

    setOptions(xOptions);
  }, [cotacoes, ajustado, createChartOptions]);

  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }

  if (validators.isNull(options)) {
    return null;
  }

  return (
    <Stack>
      <Card>
        <CardHeader
          disableTypography
          sx={{
            '& .MuiCardHeader-content': {
              minWidth: 0
            }
          }}
          title={
            <Stack
              flexGrow={1}
              direction={{ sm: 'column', md: 'row' }}
              alignItems="flex-start"
              spacing={2}>
              <Stack flexGrow={1} spacing={1} sx={{ width: '100%' }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Stack width={21} flexShrink={0}>
                    <Icon iconName="chart_line" color="primary" size={21} />
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography
                      variant="subtitle1"
                      color="textPrimary"
                      sx={{ fontWeight: 500 }}
                      noWrap>
                      {xChartConfig.title}
                    </Typography>
                    <Tooltip title="Considera agrupamentos e desdobramentos">
                      <span>
                        <Icon size={16} color={'primary'} iconName="information" />
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                {!validators.isEmpty(xResumoCotacao) ? (
                  <TableContainer sx={{ overflow: 'auto' }}>
                    <Table sx={{ width: '100%' }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              width: '1%',
                              whiteSpace: 'nowrap',
                              backgroundColor: pTheme => pTheme.palette.secondary.dark
                            }}
                          />
                          {xResumoCotacao.map(pItem => (
                            <TableCell
                              align="right"
                              key={pItem.id}
                              sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                              <Typography variant="caption" color="textSecondary" noWrap>
                                {pItem.label}
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell aria-hidden sx={{ p: 0 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              width: '1%',
                              whiteSpace: 'nowrap',
                              backgroundColor: pTheme => pTheme.palette.secondary.dark
                            }}>
                            <Stack
                              direction="row"
                              gap={1}
                              alignItems="center"
                              sx={{ '& img': { display: 'block' } }}>
                              {ativo?.logo_pessoa_id && (
                                <PessoaImage
                                  logoPessoaId={ativo.logo_pessoa_id}
                                  alt={ativo.pessoa || ativo.ativo}
                                  hasLogo={ativo.logo_ok}
                                  size={18}
                                />
                              )}
                              <Typography
                                variant="body2"
                                color="textPrimary"
                                sx={{ fontWeight: 500 }}
                                noWrap>
                                {xAtivoLabel}
                              </Typography>
                            </Stack>
                          </TableCell>
                          {xResumoCotacao.map(pItem => (
                            <TableCell
                              align="right"
                              key={pItem.id}
                              sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                              {pItem.values.map(pValue => (
                                <Typography
                                  key={pValue.id}
                                  variant="body2"
                                  color="textPrimary"
                                  sx={{ fontWeight: 500 }}
                                  noWrap>
                                  {pValue.currentValue}
                                </Typography>
                              ))}
                            </TableCell>
                          ))}
                          <TableCell aria-hidden sx={{ p: 0 }} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null}
              </Stack>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack direction="row" alignItems="flex-end" spacing={2} p={2}>
            <Controls
              range={{
                selected: options.rangeSelector.selected,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect
              }}
              ajustado={{
                onChange: setAjustado
              }}
            />
          </Stack>
          <Stack sx={{ p: 2 }}>
            <StocksChart
              key={'cotacao'}
              ref={chartRef}
              options={options}
              isLoading={isIndicadorLoading}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoCotacao;
